import React from "react";
import { Flex, Grid, Text } from "@chakra-ui/react";
import CustomApprovalModal from "@/shared/components/custom-modal";
import CustomIcon from "../../../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import { ORDER_DETAIL_KEY } from "@/features/pathpick/features/order-history/features/detail/constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  orderNumber: string;
  // 확정 수량이 남은 상품 수("2종")
  confirmedKindCount: number;
  // 확정 수량 합계("총 3개")
  confirmedQuantity: number;
  onConfirm: () => void;
  // 접수 중 — 확인 버튼을 잠가 이중 신청을 막는다.
  isRequesting?: boolean;
}

// 생성 파일은 고정 2종이고 고를 수 없다 — 담은 상품 페이지의 파일 신청(양식 선택)과 다른 점.
// 확인 버튼의 "N개"도 이 목록 길이에서 나온다.
const GENERATED_FILE_KEYS = [
  {
    titleKey: "kream_modal_file_bid_title",
    descKey: "kream_modal_file_bid_desc",
  },
  {
    titleKey: "kream_modal_file_notice_title",
    descKey: "kream_modal_file_notice_desc",
  },
] as const;

// 채워진 체크 아이콘 전용 SVG가 없어 check 아이콘을 원형 배경에 얹어 만든다.
const IncludedCheck = () => (
  <Flex
    width={"16px"}
    height={"16px"}
    borderRadius={"50%"}
    background={"#1272EF"}
    alignItems={"center"}
    justifyContent={"center"}
    flexShrink={0}
  >
    <CustomIcon src={"check"} width={10} height={10} color={"#FFFFFF"} />
  </Flex>
);

// KREAM 상품 등록 파일 신청 모달 — 확정 상품 기준으로 어떤 파일이 생성되는지 확인시키고 신청한다.
function PathPickOrderKreamFileRequestModal({
  isOpen,
  onClose,
  orderNumber,
  confirmedKindCount,
  confirmedQuantity,
  onConfirm,
  isRequesting = false,
}: Props) {
  const { t } = useI18nHelper();

  return (
    <CustomApprovalModal
      isOpen={isOpen}
      onClose={onClose}
      // minWidth가 maxWidth를 이기고, 가운데 정렬된 모달은 왼쪽으로 넘치면
      // 스크롤로도 닿을 수 없어 화면 폭으로 클램프한다.
      minWidth={"min(560px, 90dvw)"}
      isCentered={true}
    >
      <CustomApprovalModal.Header
        onClose={onClose}
        title={t(`${ORDER_DETAIL_KEY}kream_modal_title`)}
      />
      <CustomApprovalModal.Body>
        <Flex flexDirection={"column"} gap={"16px"}>
          <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
            {t(`${ORDER_DETAIL_KEY}kream_modal_description`)}
          </Text>

          {/* 어떤 주문의 무엇을 기준으로 만드는지 못 박아 두는 요약 */}
          <Grid
            templateColumns={{ base: "1fr", md: "1fr 1fr" }}
            gap={"16px"}
            padding={"12px 16px"}
            borderRadius={"8px"}
            border={"0.5px solid #D4D4D1"}
            background={"#FCFCFB"}
          >
            <Flex flexDirection={"column"} gap={"4px"}>
              <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                {t(`${ORDER_DETAIL_KEY}kream_modal_order_number`)}
              </Text>
              <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
                {orderNumber}
              </Text>
            </Flex>
            <Flex flexDirection={"column"} gap={"4px"}>
              <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                {t(`${ORDER_DETAIL_KEY}kream_modal_confirmed_products`)}
              </Text>
              <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
                {t(`${ORDER_DETAIL_KEY}kream_modal_confirmed_summary`, {
                  kinds: confirmedKindCount,
                  count: confirmedQuantity,
                })}
              </Text>
            </Flex>
          </Grid>

          <Flex flexDirection={"column"} gap={"8px"}>
            <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
              {t(`${ORDER_DETAIL_KEY}kream_modal_files_title`)}
            </Text>
            {GENERATED_FILE_KEYS.map(({ titleKey, descKey }) => (
              <Flex
                key={titleKey}
                alignItems={"center"}
                justifyContent={"space-between"}
                gap={"12px"}
                padding={"12px"}
                borderRadius={"8px"}
                border={"0.5px solid #D4D4D1"}
                background={"#FFFFFF"}
              >
                <Flex gap={"8px"} alignItems={"center"}>
                  <CustomIcon
                    src={"file-csv"}
                    width={20}
                    height={20}
                    color={"#8F8F8C"}
                  />
                  <Flex flexDirection={"column"} gap={"2px"}>
                    <Text textStyle={"body-sm-bold"} color={"#1D1D1B"}>
                      {t(`${ORDER_DETAIL_KEY}${titleKey}`)}
                    </Text>
                    <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
                      {t(`${ORDER_DETAIL_KEY}${descKey}`)}
                    </Text>
                  </Flex>
                </Flex>
                <Flex gap={"4px"} alignItems={"center"} flexShrink={0}>
                  <IncludedCheck />
                  <Text textStyle={"body-sm-bold"} color={"#1272EF"}>
                    {t(`${ORDER_DETAIL_KEY}kream_modal_file_included`)}
                  </Text>
                </Flex>
              </Flex>
            ))}
          </Flex>

          <PathPickInlineNotice tone={"blue"} leadingIcon={"info-circle"}>
            {t(`${ORDER_DETAIL_KEY}kream_modal_info`)}
          </PathPickInlineNotice>
          <PathPickInlineNotice
            tone={"yellow"}
            leadingIcon={"warning-triangle"}
          >
            {t(`${ORDER_DETAIL_KEY}kream_modal_warning`)}
          </PathPickInlineNotice>
        </Flex>
      </CustomApprovalModal.Body>
      <CustomApprovalModal.Footer
        cancelName={t("common:cancel")}
        confirmName={t(`${ORDER_DETAIL_KEY}kream_modal_submit`, {
          count: GENERATED_FILE_KEYS.length,
        })}
        onCancel={onClose}
        onConfirm={onConfirm}
        isLoading={isRequesting}
        isConfirmDisabled={isRequesting}
        confirmBtnProps={{ width: { base: "auto", md: "150px" } }}
      />
    </CustomApprovalModal>
  );
}

export default PathPickOrderKreamFileRequestModal;
